import { useEffect, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/client/lib/auth";
import { levelRank, type StaffResource } from "@/shared/permissions";
import { AdminShell } from "@/client/components/admin/AdminShell";
import { AdminPageHeader } from "@/client/components/admin/AdminPageHeader";
import { Button } from "@/client/components/ui/button";
import { Input } from "@/client/components/ui/input";
import { Checkbox } from "@/client/components/ui/checkbox";
import {
  Dialog,
  DialogPopup,
  DialogHeader,
  DialogTitle,
  DialogPanel,
  DialogFooter,
} from "@/client/components/ui/dialog";
import {
  SearchIcon,
  Columns3Icon,
  DownloadIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsUpDownIcon,
  ChevronDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  AlertCircleIcon,
  FilterIcon,
  XIcon,
} from "lucide-react";
import {
  CONSOLE_DATASETS,
  CONSOLE_BASE,
  datasetFromSlug,
  datasetHref,
} from "@/client/lib/console-datasets";
import {
  buildCsv,
  downloadCsv,
  downloadExcel,
  downloadGroupedZip,
  columnFilterValue,
  useColumnConfig,
  getExportFormat,
  setExportFormat,
  type ColumnDef,
  type DatasetDescriptor,
  type ExportFormat,
} from "@/client/lib/console";

const PAGE_SIZE = 50;
const cmp = (a: unknown, b: unknown) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1; // nulls last
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), "nl", { numeric: true });
};

// ── A tiny anchored popover, rendered in a portal so the table's overflow never clips it. ──
function AnchoredPopover({
  rect,
  onClose,
  width = 232,
  align = "start",
  children,
}: {
  rect: DOMRect;
  onClose: () => void;
  width?: number;
  align?: "start" | "end";
  children: ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const left = align === "end" ? rect.right - width : rect.left;
  const x = Math.max(8, Math.min(left, window.innerWidth - width - 8));
  const y = Math.min(rect.bottom + 4, window.innerHeight - 16);

  return createPortal(
    <>
      <div className="fixed inset-0 z-40" onMouseDown={onClose} />
      <div
        className="fixed z-50 overflow-hidden rounded-lg border bg-popover shadow-lg"
        style={{ left: x, top: y, width }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}

// ── Per-column value filter: distinct values (with counts) + search; empty selection = no filter. ──
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ColumnFilterContent({ col, rows, selected, onChange }: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  col: ColumnDef<any>;
  rows: Record<string, unknown>[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const [q, setQ] = useState("");
  const values = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows) {
      const v = columnFilterValue(col, r);
      counts.set(v, (counts.get(v) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0], "nl", { numeric: true }));
  }, [rows, col]);
  const needle = q.trim().toLowerCase();
  const shown = needle ? values.filter(([v]) => v.toLowerCase().includes(needle)) : values;

  function toggle(v: string) {
    const next = new Set(selected);
    next.has(v) ? next.delete(v) : next.add(v);
    onChange(next);
  }

  return (
    <div className="text-[12px]">
      <div className="border-b p-1.5">
        <Input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Zoek waarde..."
          startIcon={<SearchIcon />}
          className="h-7"
          size="sm"
          autoFocus
        />
      </div>
      <div className="flex items-center justify-between px-2 py-1 text-[11px] text-muted-foreground">
        <button
          type="button"
          onClick={() => onChange(new Set())}
          className="cursor-pointer rounded px-1 font-medium hover:text-foreground disabled:opacity-50"
          disabled={selected.size === 0}
        >
          Alles tonen
        </button>
        <span>{selected.size ? `${selected.size} gekozen` : "Geen filter"}</span>
      </div>
      <div className="max-h-56 overflow-y-auto p-1">
        {shown.map(([v, c]) => (
          <label key={v} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-accent">
            <Checkbox checked={selected.has(v)} onCheckedChange={() => toggle(v)} className="shrink-0" />
            <span className="flex-1 truncate">{v}</span>
            <span className="shrink-0 tabular-nums text-muted-foreground">{c}</span>
          </label>
        ))}
        {shown.length === 0 && <p className="px-2 py-3 text-center text-muted-foreground">Geen waarden.</p>}
      </div>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ConsoleView({ descriptor }: { descriptor: DatasetDescriptor<any> }) {
  type Row = Record<string, unknown>;
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [activeQuick, setActiveQuick] = useState<Set<string>>(new Set());
  const [colFilters, setColFilters] = useState<Record<string, Set<string>>>({});
  const [filterMenu, setFilterMenu] = useState<{ id: string; rect: DOMRect } | null>(null);
  const [sort, setSort] = useState<{ id: string; dir: "asc" | "desc" }>(descriptor.defaultSort ?? { id: descriptor.columns[0]!.id, dir: "asc" });
  const [page, setPage] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [exportFmt, setExportFmt] = useState<ExportFormat>(getExportFormat);

  const { visibleIds, setVisibleIds, reset } = useColumnConfig(descriptor.key, descriptor.columns);
  const visibleSet = useMemo(() => new Set(visibleIds), [visibleIds]);
  const visibleColumns = useMemo(
    () => descriptor.columns.filter((c) => visibleSet.has(c.id)),
    [descriptor.columns, visibleSet],
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    setPage(0);
    setSearch("");
    setActiveQuick(new Set());
    setColFilters({});
    setFilterMenu(null);
    setSort(descriptor.defaultSort ?? { id: descriptor.columns[0]!.id, dir: "asc" });
    (async () => {
      try {
        const res = await fetch(`/api/admin/console/${descriptor.key}`, { credentials: "include" });
        if (!res.ok) throw new Error("load");
        const data = await res.json();
        if (active) setRows(Array.isArray(data.rows) ? data.rows : []);
      } catch {
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [descriptor]);

  const activeFilterCols = useMemo(
    () => Object.entries(colFilters).filter(([, set]) => set.size > 0).map(([id]) => id),
    [colFilters],
  );

  const sortCol = descriptor.columns.find((c) => c.id === sort.id);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = rows;
    if (q) out = out.filter((r) => descriptor.searchText(r).includes(q));
    if (activeQuick.size) {
      const preds = (descriptor.quickFilters ?? []).filter((f) => activeQuick.has(f.id));
      out = out.filter((r) => preds.every((f) => f.predicate(r)));
    }
    for (const id of activeFilterCols) {
      const set = colFilters[id]!;
      const col = descriptor.columns.find((c) => c.id === id);
      if (!col) continue;
      out = out.filter((r) => set.has(columnFilterValue(col, r)));
    }
    const col = sortCol;
    if (col) {
      const val = (r: Row) => (col.sortValue ? col.sortValue(r) : col.accessor(r));
      out = [...out].sort((a, b) => {
        const c = cmp(val(a), val(b));
        return sort.dir === "asc" ? c : -c;
      });
    }
    return out;
  }, [rows, search, activeQuick, colFilters, activeFilterCols, sort, sortCol, descriptor]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  useEffect(() => { if (page > pageCount - 1) setPage(0); }, [pageCount, page]);

  function toggleSort(id: string) {
    setSort((s) => (s.id === id ? { id, dir: s.dir === "asc" ? "desc" : "asc" } : { id, dir: "asc" }));
  }
  function setColFilter(id: string, next: Set<string>) {
    setColFilters((f) => ({ ...f, [id]: next }));
    setPage(0);
  }
  function clearColFilter(id: string) {
    setColFilters((f) => { const n = { ...f }; delete n[id]; return n; });
    setPage(0);
  }
  function exportSuffix(): string {
    const filterEntries = Object.entries(colFilters).filter(([, s]) => s.size > 0);
    if (filterEntries.length === 1 && filterEntries[0]?.[1].size === 1) {
      const val = [...filterEntries[0]![1]][0];
      if (val && val !== "—") return `-${val.toLowerCase().replace(/[^a-z0-9à-ɏ]+/gi, "-").replace(/^-|-$/g, "").slice(0, 40)}`;
    }
    return "";
  }
  function doExport() {
    const date = new Date().toISOString().slice(0, 10);
    const base = `${descriptor.csvName}${exportSuffix()}-${date}`;
    if (exportFmt === "csv") {
      downloadCsv(`${base}.csv`, buildCsv(visibleColumns, filtered));
    } else {
      downloadExcel(`${base}.xlsx`, visibleColumns, filtered);
    }
  }
  function exportZip() {
    if (!descriptor.groupByColumn) return;
    downloadGroupedZip(descriptor.csvName, visibleColumns, filtered, descriptor.groupByColumn.id, descriptor.groupByColumn.label);
  }

  const menuCol = filterMenu ? descriptor.columns.find((c) => c.id === filterMenu.id) : null;

  return (
    <div className="flex min-w-0 flex-col">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 pb-3">
        <Input
          type="search"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          placeholder="Zoeken..."
          startIcon={<SearchIcon />}
          className="h-7 w-56"
          size="sm"
        />
        {(descriptor.quickFilters ?? []).map((f) => {
          const on = activeQuick.has(f.id);
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => { setActiveQuick((s) => { const n = new Set(s); n.has(f.id) ? n.delete(f.id) : n.add(f.id); return n; }); setPage(0); }}
              className={`h-7 cursor-pointer rounded-md border px-2 text-xs font-medium transition-colors ${on ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-accent hover:text-foreground"}`}
            >
              {f.label}
            </button>
          );
        })}
        <div className="ms-auto flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setPickerOpen(true)}>
            <Columns3Icon className="size-3.5" /> Kolommen
          </Button>
          <select
            value={exportFmt}
            onChange={(e) => { const v = e.target.value as ExportFormat; setExportFmt(v); setExportFormat(v); }}
            className="h-7 cursor-pointer rounded-md border border-border bg-background px-1.5 text-xs text-foreground"
          >
            <option value="xlsx">Excel</option>
            <option value="csv">CSV</option>
          </select>
          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={doExport} disabled={!filtered.length}>
            <DownloadIcon className="size-3.5" /> Export
          </Button>
          {descriptor.groupByColumn && (
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={exportZip} disabled={!filtered.length}>
              <DownloadIcon className="size-3.5" /> Per {descriptor.groupByColumn.label}
            </Button>
          )}
        </div>
      </div>

      {/* Active column-filter chips */}
      {activeFilterCols.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 pb-3">
          {activeFilterCols.map((id) => {
            const col = descriptor.columns.find((c) => c.id === id);
            const set = colFilters[id]!;
            return (
              <span key={id} className="inline-flex h-6 items-center gap-1 rounded-md border border-primary/40 bg-primary/10 px-1.5 text-[11px] font-medium text-primary">
                <FilterIcon className="size-3" />
                {col?.label}: {set.size === 1 ? [...set][0] : `${set.size} waarden`}
                <button type="button" aria-label="Filter wissen" className="cursor-pointer rounded hover:bg-primary/20" onClick={() => clearColFilter(id)}>
                  <XIcon className="size-3" />
                </button>
              </span>
            );
          })}
          <button type="button" className="h-6 cursor-pointer rounded-md px-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground" onClick={() => { setColFilters({}); setPage(0); }}>
            Alles wissen
          </button>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="animate-pulse space-y-2 rounded-lg border p-3">
          {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="h-6 w-full rounded bg-muted" />)}
        </div>
      ) : error ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-6 text-sm">
          <p className="flex items-center gap-2 font-medium text-destructive"><AlertCircleIcon size={16} /> Kan de gegevens niet laden</p>
        </div>
      ) : (
        <>
          <div className="overflow-auto rounded-lg border">
            <table className="w-max min-w-full border-separate border-spacing-0 text-[12px] leading-4">
              <thead className="sticky top-0 z-20 bg-background">
                <tr>
                  {visibleColumns.map((col, i) => {
                    const sorted = sort.id === col.id;
                    const filterActive = (colFilters[col.id]?.size ?? 0) > 0;
                    const canFilter = col.filterable !== false;
                    return (
                      <th
                        key={col.id}
                        onClick={() => toggleSort(col.id)}
                        className={`group h-8 cursor-pointer select-none whitespace-nowrap border-b bg-muted/40 px-2 text-[11px] font-semibold text-muted-foreground hover:text-foreground ${col.align === "right" ? "text-right" : "text-left"} ${i === 0 ? "sticky left-0 z-30 bg-muted/40" : ""}`}
                      >
                        <span className="inline-flex items-center gap-1">
                          {col.align === "right" && (sorted ? (sort.dir === "asc" ? <ArrowUpIcon size={11} /> : <ArrowDownIcon size={11} />) : <ChevronsUpDownIcon size={11} className="opacity-40" />)}
                          {col.label}
                          {col.align !== "right" && (sorted ? (sort.dir === "asc" ? <ArrowUpIcon size={11} /> : <ArrowDownIcon size={11} />) : <ChevronsUpDownIcon size={11} className="opacity-40" />)}
                          {canFilter && (
                            <button
                              type="button"
                              aria-label={`Filter ${col.label}`}
                              onClick={(e) => { e.stopPropagation(); setFilterMenu({ id: col.id, rect: e.currentTarget.getBoundingClientRect() }); }}
                              className={`cursor-pointer rounded p-0.5 transition-colors hover:text-foreground ${filterActive ? "text-primary" : "text-muted-foreground/50 opacity-0 group-hover:opacity-100"}`}
                            >
                              <FilterIcon size={11} className={filterActive ? "fill-primary/25" : ""} />
                            </button>
                          )}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row, i) => {
                  const href = descriptor.getRowHref?.(row);
                  return (
                    <tr
                      key={`${descriptor.getRowId(row)}#${page * PAGE_SIZE + i}`}
                      onClick={href ? () => navigate(href) : undefined}
                      className={`group h-7 ${href ? "cursor-pointer" : ""}`}
                    >
                      {visibleColumns.map((col, i) => (
                        <td
                          key={col.id}
                          className={`h-7 max-w-[280px] truncate border-b px-2 align-middle group-hover:bg-muted/50 ${col.align === "right" ? "text-right tabular-nums" : ""} ${col.mono ? "font-mono text-[11px]" : ""} ${i === 0 ? "sticky left-0 z-10 bg-background font-medium text-foreground" : "text-foreground"}`}
                          title={typeof col.accessor(row) === "string" ? String(col.accessor(row)) : undefined}
                        >
                          {col.render ? col.render(row) : renderCell(col.accessor(row))}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {pageRows.length === 0 && (
                  <tr>
                    <td colSpan={visibleColumns.length} className="px-3 py-10 text-center text-sm text-muted-foreground">
                      Geen resultaten.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Totals + pagination */}
          <div className="mt-2 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
            <span>
              {filtered.length} {filtered.length === 1 ? "rij" : "rijen"}
              {filtered.length !== rows.length ? ` (van ${rows.length})` : ""}
            </span>
            {pageCount > 1 && (
              <div className="flex items-center gap-2">
                <span>Pagina {page + 1} / {pageCount}</span>
                <Button size="sm" variant="outline" className="h-7 px-2" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                  <ChevronLeftIcon size={14} />
                </Button>
                <Button size="sm" variant="outline" className="h-7 px-2" disabled={page >= pageCount - 1} onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}>
                  <ChevronRightIcon size={14} />
                </Button>
              </div>
            )}
          </div>
        </>
      )}

      {filterMenu && menuCol && (
        <AnchoredPopover rect={filterMenu.rect} onClose={() => setFilterMenu(null)}>
          <ColumnFilterContent
            col={menuCol}
            rows={rows}
            selected={colFilters[menuCol.id] ?? new Set<string>()}
            onChange={(next) => setColFilter(menuCol.id, next)}
          />
        </AnchoredPopover>
      )}

      <ColumnPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        columns={descriptor.columns}
        visibleSet={visibleSet}
        onToggle={(id) => {
          const next = new Set(visibleSet);
          next.has(id) ? next.delete(id) : next.add(id);
          setVisibleIds(descriptor.columns.filter((c) => next.has(c.id)).map((c) => c.id));
        }}
        onReset={reset}
      />
    </div>
  );
}

function renderCell(v: unknown) {
  if (v == null || v === "") return <span className="text-muted-foreground/50">—</span>;
  if (typeof v === "boolean") return v ? "Ja" : "Nee";
  return String(v);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ColumnPicker({ open, onClose, columns, visibleSet, onToggle, onReset }: {
  open: boolean;
  onClose: () => void;
  columns: ColumnDef<any>[];
  visibleSet: Set<string>;
  onToggle: (id: string) => void;
  onReset: () => void;
}) {
  const groups = useMemo(() => {
    const m = new Map<string, ColumnDef<any>[]>();
    for (const c of columns) {
      const g = c.group ?? "Overig";
      if (!m.has(g)) m.set(g, []);
      m.get(g)!.push(c);
    }
    return [...m.entries()];
  }, [columns]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogPopup className="w-[92vw] max-w-md">
        <DialogHeader>
          <DialogTitle>Kolommen</DialogTitle>
        </DialogHeader>
        <DialogPanel>
          <div className="max-h-[60vh] space-y-4 overflow-y-auto">
            {groups.map(([group, cols]) => (
              <div key={group}>
                <p className="mb-1.5 text-[11px] font-semibold text-muted-foreground">{group}</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {cols.map((c) => (
                    <label key={c.id} className={`flex items-center gap-2 text-sm ${c.alwaysVisible ? "opacity-60" : "cursor-pointer"}`}>
                      <Checkbox
                        checked={visibleSet.has(c.id)}
                        disabled={c.alwaysVisible}
                        onCheckedChange={() => onToggle(c.id)}
                        className="shrink-0"
                      />
                      <span className="truncate">{c.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DialogPanel>
        <DialogFooter>
          <Button variant="outline" onClick={onReset}>Standaard herstellen</Button>
          <Button onClick={onClose}>Klaar</Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}

// ── Breadcrumb dataset switcher: the last crumb is a dropdown to jump between datasets. ──
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function DatasetSwitcher({ current, accessible }: { current: DatasetDescriptor<any>; accessible: DatasetDescriptor<any>[] }) {
  const navigate = useNavigate();
  const [rect, setRect] = useState<DOMRect | null>(null);

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          // Read the rect synchronously: React nulls e.currentTarget once the handler returns, so
          // touching it inside the (possibly deferred) state updater would throw on reopen.
          if (rect) { setRect(null); return; }
          setRect(e.currentTarget.getBoundingClientRect());
        }}
        className="inline-flex cursor-pointer items-center gap-1 rounded-md px-1.5 py-0.5 font-medium text-foreground transition-colors hover:bg-accent"
        aria-haspopup="menu"
      >
        {current.shortLabel ?? current.label}
        <ChevronDownIcon className="size-3.5 text-muted-foreground" />
      </button>
      {rect && (
        <AnchoredPopover rect={rect} onClose={() => setRect(null)} width={220}>
          <div className="max-h-[70vh] overflow-y-auto p-1">
            {accessible.map((d) => {
              const active = d.key === current.key;
              return (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => { setRect(null); navigate(datasetHref(d)); }}
                  className={`flex w-full cursor-pointer items-center rounded-md px-2 py-1.5 text-left text-[13px] transition-colors ${active ? "bg-primary/10 font-medium text-primary" : "text-foreground hover:bg-accent"}`}
                >
                  {d.shortLabel ?? d.label}
                </button>
              );
            })}
          </div>
        </AnchoredPopover>
      )}
    </>
  );
}

export function ScholenConsolePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { seg } = useParams<{ seg?: string }>();
  const isAdmin = !!user?.roles.includes("admin");
  const caps = user?.capabilities;
  // Only show datasets the user may view (admin = all). The server also gates per-dataset.
  const accessible = useMemo(
    () => CONSOLE_DATASETS.filter((d) => isAdmin || (caps ? levelRank(caps[d.resource as StaffResource] ?? "none") >= 1 : false)),
    [isAdmin, caps],
  );

  const requested = datasetFromSlug(seg);
  const allowed = accessible.some((d) => d.key === requested.key);
  const descriptor = allowed ? requested : accessible[0] ?? CONSOLE_DATASETS[0]!;

  // If the URL points at a dataset this user can't see, bounce to their first accessible one.
  useEffect(() => {
    if (!allowed && accessible.length) navigate(datasetHref(descriptor), { replace: true });
  }, [allowed, accessible.length, descriptor, navigate]);

  return (
    <AdminShell>
      <AdminPageHeader
        items={[
          { label: "Dataconsole", to: CONSOLE_BASE },
          { node: <DatasetSwitcher current={descriptor} accessible={accessible} /> },
        ]}
        subtitle={descriptor.description}
      />
      <ConsoleView descriptor={descriptor} />
    </AdminShell>
  );
}
